import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tile } from "@react-gcc-eds/core";

import { IWeekMetric, useStore } from "../../../../store";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { parse } from "date-fns";
import { maxMetricCreationDate, minMetricCreationDate } from "../utils";
import CustomTabs, { ITab } from "../../../shared/custom-tabs";
import { DashboardGraph, IDashboardItemSelected } from "..";
import { DashboardTileAlignment as TileAlignment } from "../index";
import { KpiContainer } from "../../helpers/dashboard-kpis/kpi-container";
import { useLocalization } from "../../../../utils/localization";
import { DashboardTooltipHeader } from "../../../shared/tooltip-header";

const ConfirmedDeliveryDate = ({
  labels,
  className,
  customerId,
  alignment,
  onItemSelected
}: {
  labels: { [key: string]: string | undefined };
  className: string;
  customerId: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const [state] = useStore();
  const tileRef = useRef<HTMLDivElement>(null);
  const { orderConfirmedDeliveriesMetric, itemConfirmedDeliveriesMetric } =
    state.fetchingData.metrics;
  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);
  const { localeString } = useLocalization();

  const getTabs = useCallback(() => {
    const tabs = [];
    if (labels["purchaseOrders"] !== undefined) {
      tabs.push(orderTab);
    }
    if (labels["purchaseOrderItems"] !== undefined) {
      tabs.push(itemsTab);
    }
    return tabs;
  }, [orderConfirmedDeliveriesMetric, itemConfirmedDeliveriesMetric]);

  const { getOrderConfirmedDeliveriesMetric, getItemConfirmedDeliveriesMetric } =
    useMetricsActions();

  useEffect(() => {
    getOrderConfirmedDeliveriesMetric();
    getItemConfirmedDeliveriesMetric();
  }, [customerId]);

  const handleItemSelected = (
    metric: IWeekMetric,
    allMetrics: IWeekMetric[],
    graph: DashboardGraph
  ) => {
    if (!onItemSelected) {
      return undefined;
    }

    return () => {
      onItemSelected({
        filters: {
          orderCreationRange: {
            from: minMetricCreationDate(allMetrics),
            to: maxMetricCreationDate(allMetrics)
          },
          confirmedDeliveryRange: {
            from: parse(metric.startDate, "yyyy-MM-dd", new Date(1901, 1, 1)),
            to: parse(metric.endDate, "yyyy-MM-dd", new Date(1901, 1, 1))
          }
        },
        graph
      });
    };
  };

  const orderTab = {
    view: KpiContainer({
      metricLoading: orderConfirmedDeliveriesMetric === true,
      metrics: state.metrics.orderConfirmedDeliveriesMetric,
      onKPIClick: handleItemSelected,
      kpiBottomText: "Confirmed"
    }),
    title: labels["purchaseOrders"],
    index: 0
  } as ITab;

  const itemsTab = {
    view: KpiContainer({
      metricLoading: itemConfirmedDeliveriesMetric === true,
      metrics: state.metrics.itemConfirmedDeliveriesMetric,
      onKPIClick: handleItemSelected,
      kpiBottomText: "Confirmed"
    }),
    title: labels["purchaseOrderItems"],
    index: 1
  } as ITab;

  const [_, setSelectedTab] = useState<ITab>(orderTab);

  const tileHeader = (
    <DashboardTooltipHeader
      header={localeString("Dashboard_ConfirmedDeliveryDate") || "Confirmed Delivery Date"}
      tooltipInfo="What has been confirmed for delivery"
      alignment={alignment}
    />
  );

  return (
    <>
      <Tile
        className={className}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        title={tileHeader}
      >
        <CustomTabs onTabChange={(tab: ITab) => setSelectedTab(tab)} tabs={getTabs()} />
      </Tile>
    </>
  );
};

export default ConfirmedDeliveryDate;
